import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';

import { renderDateTitle, renderTypeTitle } from '../utils';

const useStyles = makeStyles(() => ({
    selectorContainer: {
        display: 'flex',
        gap: '20px',
        margin: '20px 0'
    },
    selectorBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        fontSize: '16px',
        fontWeight: '700',
        color: '#1F2152',
        minWidth: 'fit-content',
        padding: '10px 20px',
        border: '1px solid #E3E3E3',
        borderRadius: '100px 0 0 100px',
        textTransform: 'none',
        '& span': {
            gap: '20px'
        }
    },
    selectorBtnRight: {
        borderRadius: '0 100px 100px 0',
        height: '100%'
    },
    selectorBtnActive: {
        backgroundColor: '#7378E8',
        color: '#FFFFFF',
        border: '1px solid #7378E8'
    },
    splitButton: {
        display: 'flex',
        alignItems: 'center'
    }
}));

type SelectorsProps = {
    dateButtonActive: boolean;
    setDateButtonActive: (active: boolean) => void;
    typeButtonActive: boolean;
    setTypeButtonActive: (active: boolean) => void;
    filterDate: Date | null;
    filterType: string;
    setFilterEndDate: (date: Date | null) => void;
    setFilterDate: (date: Date | null) => void;
    setFilterType: (type: string) => void;
    setViewFilter: (filter: string | null) => void;
};

const Selectors = ({
    dateButtonActive,
    setDateButtonActive,
    typeButtonActive,
    setTypeButtonActive,
    filterDate,
    filterType,
    setFilterEndDate,
    setFilterDate,
    setFilterType,
    setViewFilter
}: SelectorsProps) => {
    const classes = useStyles();

    const handleCancelClick = (
        filter: string,
        setActive: (active: boolean) => void
    ) => {
        if (filter === 'filter date') {
            setFilterEndDate(null);
            setFilterDate(null);
        }

        if (filter === 'filter type') {
            setFilterType('all');
        }

        setActive(false);
        setViewFilter(null);
    };

    const handleSelectClick = (
        filter: string,
        setActive: (active: boolean) => void
    ) => {
        setViewFilter(filter);
        setActive(true);
    };

    const getFilterAriaLabel = (filter: string, label: string) => {
        const filterName = filter === 'filter date' ? 'Date' : 'Type';
        return `Select ${filterName} filter${label !== filterName ? `: ${label}` : ''}`;
    };

    const getToggleAriaLabel = (filter: string, isActive: boolean) => {
        const filterName = filter === 'filter date' ? 'Date' : 'Type';
        return isActive
            ? `Clear ${filterName} filter`
            : `Open ${filterName} filter menu`;
    };

    const renderButton = (
        label: string,
        filter: string,
        isActive: boolean,
        setActive: (active: boolean) => void
    ) => (
        <div className={classes.splitButton}>
            <Button
                className={
                    isActive
                        ? `${classes.selectorBtn} ${classes.selectorBtnActive}`
                        : classes.selectorBtn
                }
                onClick={() => handleSelectClick(filter, setActive)}
                aria-label={getFilterAriaLabel(filter, label)}
            >
                <span>{label}</span>
            </Button>
            <Button
                className={
                    isActive
                        ? `${classes.selectorBtnRight} ${classes.selectorBtnActive}`
                        : `${classes.selectorBtn} ${classes.selectorBtnRight}`
                }
                onClick={() => {
                    if (isActive) {
                        handleCancelClick(filter, setActive);
                    } else {
                        handleSelectClick(filter, setActive);
                    }
                }}
                aria-label={getToggleAriaLabel(filter, isActive)}
            >
                {isActive ? (
                    <i className="fas fa-times" />
                ) : (
                    <i className="fas fa-chevron-down" />
                )}
            </Button>
        </div>
    );

    return (
        <div className={classes.selectorContainer}>
            {renderButton(
                filterDate ? renderDateTitle(filterDate) : 'Date',
                'filter date',
                dateButtonActive,
                setDateButtonActive
            )}
            {renderButton(
                filterType ? renderTypeTitle(filterType) : 'Type',
                'filter type',
                typeButtonActive,
                setTypeButtonActive
            )}
        </div>
    );
};

export default Selectors;
